/**
 * VehicleFuelTransactionsTab - Visar bränsletransaktioner för ett fordon
 * Hämtar transaktioner via kort som är/har varit kopplade till fordonet
 * Används i VehicleCard under fliken "Kort specifikation"
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import * as fuelApi from '@/services/fuel/fuelManagementService';
import type { FuelTransaction } from '@/services/fuel/fuelManagementService';

import DateInput from '@/components/common/DateInput';
import FuelDuplicateBadge from '@/components/fuel/FuelDuplicateBadge';

interface VehicleFuelTransactionsTabProps {
  vehicleId: number;
}

function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function VehicleFuelTransactionsTab({ vehicleId }: VehicleFuelTransactionsTabProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<FuelTransaction[]>([]);
  const [outsidePeriodCount, setOutsidePeriodCount] = useState(0);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return localYmd(d);
  });
  const [dateTo, setDateTo] = useState(() => localYmd(new Date()));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fuelApi.getVehicleTransactions(vehicleId, dateFrom, dateTo);
      setTransactions(data);
      if (data.length === 0) {
        const all = await fuelApi.getVehicleTransactions(vehicleId);
        setOutsidePeriodCount(all.length);
      } else {
        setOutsidePeriodCount(0);
      }
    } catch {
      setLoadError(t('fuel.vehicleCard.loadTxError'));
      setTransactions([]);
      setOutsidePeriodCount(0);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, dateFrom, dateTo, t]);

  useEffect(() => { load(); }, [load]);

  const toNum = (v: unknown): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmt = (n: unknown) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(toNum(n));

  // API returnerar DECIMAL som sträng — tvinga Number så reduce inte konkatenerar → NaN
  const uniqueTx = transactions.filter((tx) => !tx.cross_source_duplicate);
  const totalQty = uniqueTx.reduce((s, tx) => s + toNum(tx.quantity), 0);
  const totalCost = uniqueTx.reduce(
    (s, tx) => s + toNum(tx.price_excl_vat) + toNum(tx.vat),
    0
  );

  return (
    <div className="space-y-4 text-textDefault">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">{t('common.from')}</label>
          <DateInput value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-1.5 border border-border rounded text-sm bg-bgBase text-textDefault" />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">{t('common.to')}</label>
          <DateInput value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-1.5 border border-border rounded text-sm bg-bgBase text-textDefault" />
        </div>
      </div>

      {loading ? (
        <p className="text-textSecondary text-center py-6 text-sm">{t('common.loading')}</p>
      ) : loadError ? (
        <p className="text-red-600 text-center py-6 text-sm">{loadError}</p>
      ) : transactions.length === 0 ? (
        <p className="text-textSecondary text-center py-6 text-sm">
          {outsidePeriodCount > 0
            ? t('fuel.vehicleCard.outsidePeriod', { count: outsidePeriodCount })
            : t('fuel.vehicleCard.noTransactions')}
        </p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-textSecondary text-xs border-b border-border">
                <th className="px-3 py-2">{t('fuel.invoices.date')}</th>
                <th className="px-3 py-2">{t('fuel.invoices.company')}</th>
                <th className="px-3 py-2">{t('fuel.invoices.station')}</th>
                <th className="px-3 py-2">{t('fuel.invoices.product')}</th>
                <th className="px-3 py-2 text-right">{t('fuel.invoices.quantity')}</th>
                <th className="px-3 py-2 text-right">{t('fuel.invoices.priceExclVat')}</th>
                <th className="px-3 py-2 text-right">{t('fuel.invoices.vatAmount')}</th>
                <th className="px-3 py-2">{t('fuel.cards.cardNumber')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => (
                <tr
                  key={tx.fuel_id}
                  className={`border-b border-border ${tx.cross_source_duplicate ? 'bg-amber-50' : ''}`}
                >
                  <td className="px-3 py-2">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {tx.purchased ? new Date(tx.purchased).toLocaleString('sv-SE') : '-'}
                      {tx.cross_source_duplicate ? (
                        <FuelDuplicateBadge company={tx.original_company_name} />
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2">{tx.fuel_company_name || tx.source_station_company || '-'}</td>
                  <td className="px-3 py-2">{tx.station_name || '-'}</td>
                  <td className="px-3 py-2">{tx.product_name || '-'}</td>
                  <td className="px-3 py-2 text-right">{tx.quantity ? fmt(tx.quantity) + ' L' : '-'}</td>
                  <td className="px-3 py-2 text-right">{fmt(tx.price_excl_vat)} kr</td>
                  <td className="px-3 py-2 text-right">{fmt(tx.vat)} kr</td>
                  <td className="px-3 py-2 font-mono text-xs">{tx.card_number || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end gap-6 text-sm font-medium border-t border-border pt-3 text-textDefault">
            <span>{t('fuel.vehicleCard.totalLiters')}: {fmt(totalQty)} L</span>
            <span>{t('fuel.vehicleCard.totalCost')}: {fmt(totalCost)} kr</span>
          </div>
        </>
      )}
    </div>
  );
}
